'use client';

import ControlledButton from '@umodeapporg/ui/components/Form/ControlledButton';
import LoadingSpinner from '@umodeapporg/ui/components/UI/LoadingSpinner';
import { MaterialSymbol } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import { useTranslation } from 'react-i18next';

import { Notice } from '@/components/surface/Notice';

interface IAvisoDatasNoVestoProps {
  /** Quantas OPs estão com data diferente da que foi. Quem decide mostrar é a tela. */
  comDataDiferente: number;
  exportando: boolean;
  /** Absent for a role that cannot export: the notice still says the dates differ. */
  onExportar?: () => void;
}

/**
 * "N OPs com data diferente da que foi para o VESTO", com o botão que resolve.
 *
 * O aviso existe porque a diferença é invisível: o CosturAI mostra a data nova e o mapão do
 * VESTO mostra a velha, e as duas telas parecem certas separadamente. Quem olha só uma não
 * tem como saber que elas discordam.
 *
 * Quem decide mostrar é a tela, pela contagem: com zero o aviso simplesmente não é montado,
 * e esse é o ponto. Um aviso que fica depois de resolvido deixa de ser lido.
 *
 * O MESMO componente serve o Início e a Carteira: duas cópias desta faixa já divergiriam no
 * primeiro ajuste de texto.
 */
export function AvisoDatasNoVesto({ comDataDiferente, exportando, onExportar }: IAvisoDatasNoVestoProps) {
  const { t } = useTranslation('common');

  return (
    <Notice
      tone="warning"
      icon="sync_problem"
      title={t('ops.exportarVesto.aviso.titulo', { n: comDataDiferente })}
      action={
        onExportar === undefined ? undefined : (
          <ControlledButton
            type="button"
            variant="outlined"
            tone="neutral"
            size="sm"
            onClick={onExportar}
            disabled={exportando}
            className="shrink-0"
          >
            {exportando ? (
              <LoadingSpinner size={4} color="foreground-faint" className="mr-2" />
            ) : (
              <MaterialSymbol icon="ios_share" size={16} className="mr-2" />
            )}
            {t('ops.exportarVesto.botao')}
          </ControlledButton>
        )
      }
    >
      <p className="text-xs text-foreground-muted">{t('ops.exportarVesto.aviso.texto')}</p>
    </Notice>
  );
}
